import type { FocusPageKey, FocusPresetProfile, PerPageFocusModes } from '@/lib/types';

export const FOCUS_PAGE_ORDER: FocusPageKey[] = [
  'dashboard',
  'transactions',
  'review',
  'analytics',
];

export const FOCUS_PAGE_LABELS: Record<FocusPageKey, string> = {
  dashboard: 'Dashboard',
  transactions: 'Transactions',
  review: 'Review',
  analytics: 'Analytics',
};

export const DEFAULT_FOCUS_MODES: Record<FocusPageKey, string> = {
  dashboard: 'balanced',
  transactions: 'balanced',
  review: 'balanced',
  analytics: 'balanced',
};

export function readLegacyFocusModes(): Partial<Record<FocusPageKey, string>> {
  if (typeof window === 'undefined') {
    return {};
  }

  return FOCUS_PAGE_ORDER.reduce<Partial<Record<FocusPageKey, string>>>((acc, pageKey) => {
    const raw = window.localStorage.getItem(`fintrack:focus-mode:${pageKey}`);
    if (raw) {
      acc[pageKey] = raw;
    }
    return acc;
  }, {});
}

export function getPresetMode(
  preset: FocusPresetProfile | null | undefined,
  pageKey: FocusPageKey,
): string | null {
  return preset?.perPageFocusModes?.[pageKey] ?? null;
}

export function resolveEffectiveFocusModes({
  perPageFocusModes,
  activePreset,
  legacyModes,
}: {
  perPageFocusModes?: PerPageFocusModes;
  activePreset?: FocusPresetProfile | null;
  legacyModes?: Partial<Record<FocusPageKey, string>>;
}): Record<FocusPageKey, string> {
  return FOCUS_PAGE_ORDER.reduce<Record<FocusPageKey, string>>((acc, pageKey) => {
    acc[pageKey] =
      perPageFocusModes?.[pageKey] ??
      activePreset?.perPageFocusModes?.[pageKey] ??
      legacyModes?.[pageKey] ??
      DEFAULT_FOCUS_MODES[pageKey];
    return acc;
  }, { ...DEFAULT_FOCUS_MODES });
}

export function formatRelativeTime(isoString: string): string {
  const timestamp = new Date(isoString).getTime();
  if (!Number.isFinite(timestamp)) {
    return 'Just now';
  }

  const diffMs = Date.now() - timestamp;
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;

  if (diffMs < minute) {
    return 'Just now';
  }

  if (diffMs < hour) {
    return rtf.format(-Math.floor(diffMs / minute), 'minute');
  }

  if (diffMs < day) {
    return rtf.format(-Math.floor(diffMs / hour), 'hour');
  }

  if (diffMs < week) {
    return rtf.format(-Math.floor(diffMs / day), 'day');
  }

  return rtf.format(-Math.floor(diffMs / week), 'week');
}
